import { useRef, useState } from 'react'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import {
  getPdfViewerSelectionAreaBoxInitValues,
  PDF_VIEWER_SELECTION_AREA_BOX_MIN_HEIGHT,
  PDF_VIEWER_SELECTION_AREA_BOX_MIN_WIDTH,
} from '../../PdfViewerSelectionAreaBox'

import {
  transformPercentCoordinatesToSelectAreaCoordinates,
  transformSelectAreaCoordinatesToPercentCoordinates,
} from './usePdfViewerSelectionAreaBox.utils'
import { usePdfViewerSelectionAreaBoxGestures } from './usePdfViewerSelectionAreaBoxGestures'
import { usePdfViewerSelectionAreaBoxPageSync } from './usePdfViewerSelectionAreaBoxPageSync'
import { usePdfViewerSelectionAreaBoxRect } from './usePdfViewerSelectionAreaBoxRect'

export function usePdfViewerSelectionAreaBox({
  maxHeight,
  maxWidth,
  minHeight = PDF_VIEWER_SELECTION_AREA_BOX_MIN_HEIGHT,
  minWidth = PDF_VIEWER_SELECTION_AREA_BOX_MIN_WIDTH,
} = EMPTY_OBJECT) {
  const [pdfViewerInstance, setPdfViewerInstance] = useState(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [isPdfFinishedRendering, setPdfFinishedRendering] = useState(false)

  const resizeRef = useRef(null)
  const viewerRootRef = useRef(null)

  const selectBoxRect = usePdfViewerSelectionAreaBoxRect()

  const {
    commitSelectBoxRect,
    currentPageRef,
    getCurrentPageRect,
    isInitSelectAreaRef,
    pageRectRef,
    pdfPageCoordinates,
    percentCoordinatesRef,
    selectBoxRef,
    selectedBox,
    setCoordinatesToSelectBoxStyle,
    setSelectedBox,
    updateSelectBoxRect,
  } = selectBoxRect

  function handlePageSync(nextPageNumber) {
    setPageNumber(nextPageNumber)
    setPdfFinishedRendering(true)
  }

  const { maxResizeHeightRef, maxResizeWidthRef } =
    usePdfViewerSelectionAreaBoxPageSync({
      ...selectBoxRect,
      maxHeight,
      maxWidth,
      pdfViewerInstance,
      onPageSync: handlePageSync,
    })

  const { gestureProps, isChanged, isChanging, setChanged } =
    usePdfViewerSelectionAreaBoxGestures({
      commitSelectBoxRect,
      maxResizeHeightRef,
      maxResizeWidthRef,
      minHeight,
      minWidth,
      pageRectRef,
      pdfViewerInstance,
      selectBoxRef,
      updateSelectBoxRect,
    })

  function hideSelectArea() {
    if (selectBoxRef.current) {
      selectBoxRef.current.style.display = 'none'
    }
  }

  function initSelectBoxStyle(initialCoordinates) {
    if (!selectedBox) {
      const currentPageRect = getCurrentPageRect()

      const hasInitialCoordinates = initialCoordinates.length > 0
      const coordinates = hasInitialCoordinates
        ? transformPercentCoordinatesToSelectAreaCoordinates(
            initialCoordinates,
            currentPageRect
          )
        : getPdfViewerSelectionAreaBoxInitValues(currentPageRect)

      setSelectedBox(coordinates)

      percentCoordinatesRef.current = hasInitialCoordinates
        ? initialCoordinates
        : transformSelectAreaCoordinatesToPercentCoordinates(
            coordinates,
            currentPageRect
          )

      if (selectBoxRef.current) {
        selectBoxRef.current.style.display = 'block'

        setCoordinatesToSelectBoxStyle(coordinates)

        isInitSelectAreaRef.current = true
      }
    }
  }

  function showSelectArea() {
    if (selectBoxRef.current) {
      selectBoxRef.current.style.display = 'block'

      if (!isInitSelectAreaRef.current) {
        initSelectBoxStyle(EMPTY_LIST)
      }
    }
  }

  function setDefaultParams() {
    hideSelectArea()
    setSelectedBox(null)
    isInitSelectAreaRef.current = false
    setPdfFinishedRendering(false)
  }

  function handleHideSelectedArea() {
    hideSelectArea()
    setChanged(false)
  }

  return {
    handleHideSelectedArea,
    hideSelectArea,
    initSelectBoxStyle,
    isChanged,
    isChanging,
    isInitSelectArea: isInitSelectAreaRef,
    isPdfFinishedRendering,
    pageNumber,
    pdfPageCoordinates,
    pdfPageRef: currentPageRef,
    pdfViewerInstance,
    selectAreaBoxProps: {
      ...gestureProps,
      resizeRef,
      selectBoxRef,
    },
    selectedBox,
    setChanged,
    setDefaultParams,
    setSelectedBox,
    showSelectArea,
    viewerProps: {
      setPdfViewerInstance,
      viewerRootRef,
    },
  }
}
